import { useState, useEffect, useRef } from 'react';
import { usePWAInstall } from './hooks/usePWAInstall';
import { usePWAUpdate } from './hooks/usePWAUpdate';
import { useTheme } from './hooks/useTheme';
import { useTtsAutoAdvance } from './hooks/useTtsAutoAdvance';
import { useWakeLock } from './hooks/useWakeLock';
import { saveLastRead, loadLastRead, triggerHaptic, clearLastRead } from './utils/smartReader';
import { speakVerses, stopSpeaking } from './utils/tts';
import { clearTtsProgress, loadTtsProgress } from './utils/ttsProgress';
import { logTtsEvent } from './utils/ttsController';
import { CorpusService } from './services/CorpusRegistry';
import { LibraryDB } from './services/LibraryDB';
import { LibraryView } from './components/LibraryView';
import { ReaderFlipView } from './components/ReaderFlipView';
import { DiscoverView } from './components/DiscoverView';
import { SettingsModal } from './components/SettingsModal';
import { ChatDrawer } from './components/ChatDrawer';
import { Toast } from './components/Toast';
import { HelpView } from './components/HelpView';
import './App.css';

import { AudioPlayerOverlay } from './components/AudioPlayerOverlay';
import { AdminFactory } from './components/AdminFactory';
import { ImportModal } from './components/ImportModal';
import { DeveloperConsole } from './components/admin/DeveloperConsole';
import { ErrorBoundary } from './components/ErrorBoundary';

const App = () => {
    // Hooks
    const pwa = usePWAInstall();
    const { needRefresh, updateServiceWorker } = usePWAUpdate();
    const [showChat, setShowChat] = useState(false);
    const [showSettings, setShowSettings] = useState<string | null>(null);

    // Commercial / Library Audio
    const [audioManifest, setAudioManifest] = useState<any[]>([]);

    // Book Club Errors
    const [bookClubError, setBookClubError] = useState<string | null>(null);

    // Initial Load & Entitlement
    useEffect(() => {
        // 1. Load Audio Manifest (Offline-First strategy)
        // Check IDB first for instant load
        LibraryDB.getAudioManifest().then(items => {
            if (items && items.length > 0) {
                console.log(`[App] Loaded ${items.length} items from offline manifest cache`);
                setAudioManifest(items);
            }
        }).catch(e => console.warn("Failed to load offline manifest", e));

        // 2. Fetch Fresh Manifest (Stale-While-Revalidate)
        fetch('/api/library/manifest').then(r => r.json()).then(d => {
            let items: any[] = [];
            if (Array.isArray(d)) items = d;
            else if (d.ok && d.items) items = d.items;

            if (items.length > 0) {
                setAudioManifest(items);
                // Persist to IDB
                LibraryDB.saveAudioManifest(items).catch(console.error);
            }
        }).catch(console.error);

        // Entitlement (Check once on load)
        const checkEntitlement = () => {
            fetch('/api/entitlement').then(r => r.json()).then(d => {
                if (typeof d.isPro === 'boolean') setIsPro(d.isPro);
            }).catch(console.error);
        };
        checkEntitlement();
    }, []);

    const [audioOverlay, setAudioOverlay] = useState<{ url: string, title: string, coverUrl?: string, startTime?: number, bookId?: string } | null>(null);
    const [showBookMenu, setShowBookMenu] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);

    // Book Club Membership State (P0-C)
    const [isBookClubMember, setIsBookClubMember] = useState(false); // Default false for non-members
    const [debugMemberOverride, setDebugMemberOverride] = useState(false); // Debug toggle

    // Effective Member Status
    const isMember = isBookClubMember || debugMemberOverride;

    // Refs for Play Button Long Press
    const longPressTimer = useRef<any>(null); // Type 'any' to handle NodeJS vs Browser timeout types if needed
    const isLongPress = useRef(false);

    // Refresh Entitlement when Settings close (in case PIN changed)
    useEffect(() => {
        if (!showSettings) {
            fetch('/api/entitlement').then(r => r.json()).then(d => {
                if (typeof d.isPro === 'boolean') setIsBookClubMember(d.isPro);
            }).catch(() => { });
        }
    }, [showSettings]);


    // Common State
    const { theme, setTheme } = useTheme();
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 900);

    // Router State: Initialize based on current path
    const [view, setView] = useState<'library' | 'discover' | 'reader' | 'settings' | 'help' | 'admin' | 'admin-console'>(() => {
        const path = window.location.pathname.replace(/\/$/, '');
        if (path === '/admin/factory') return 'admin';
        if (path === '/admin/console') return 'admin-console';
        return 'library';
    });

    const wakeLock = useWakeLock();

    // Removed duplicated useEffect router logic


    // Corpus
    const [activeCorpusId, setActiveCorpusId] = useState(() => CorpusService.getActive().id);
    const activeCorpus = CorpusService.getActive();

    // Chat State (Lifted)
    const [chatMessages, setChatMessages] = useState<any[]>([]);

    // Reading State
    const [currentBookId, setCurrentBookId] = useState<string>('');
    const [currentChapter, setCurrentChapter] = useState<number>(1);

    // Settings
    const [smartReader, setSmartReader] = useState(() => localStorage.getItem('bible_smart_reader') !== 'false');
    useEffect(() => { localStorage.setItem('bible_smart_reader', String(smartReader)); }, [smartReader]);

    const [haptics, setHaptics] = useState(() => localStorage.getItem('bible_haptics') !== 'false');
    useEffect(() => { localStorage.setItem('bible_haptics', String(haptics)); }, [haptics]);

    const [readerMode, setReaderMode] = useState<'scroll' | 'flip'>(() => (localStorage.getItem('bible_reader_mode') as 'scroll' | 'flip') || 'flip');
    const [toastMsg, setToastMsg] = useState<string | null>(null);

    // Content
    const [books, setBooks] = useState(() => activeCorpus.getSections());
    const [verseData, setVerseData] = useState<Record<string, string> | null>(null);
    const [pages, setPages] = useState<string[][]>([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [isPaginating, setIsPaginating] = useState(false);

    // Narrated Tip (One-time)
    useEffect(() => {
        if (!audioManifest.length || view !== 'reader') return;

        const book = books.find(b => b.id === currentBookId);
        let bookAudio = audioManifest.find(m => m.id === currentBookId);
        if (!bookAudio && book?.title?.toLowerCase().includes('siddhartha')) {
            bookAudio = audioManifest.find(m => m.id === 'siddhartha');
        }

    }, [currentBookId, audioManifest, view, books]);

    // Restore State Logic
    const [pendingRestore, setPendingRestore] = useState<{ page: number } | null>(null);

    // TTS State & Controller
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);

    // Check for resume capability
    const [resumeHint, setResumeHint] = useState<string | null>(null);
    useEffect(() => {
        if (!currentBookId) return;
        const saved = loadTtsProgress(activeCorpusId, currentBookId);
        if (saved && saved.chapter === currentChapter && saved.pageIndex === pageIndex) {
            setResumeHint(`Resume: ${saved.snippet}...`);
        } else {
            setResumeHint(null);
        }
    }, [currentBookId, currentChapter, pageIndex, activeCorpusId, isSpeaking]);

    // Refs for TTS callbacks to access fresh state
    const readerStateRef = useRef({ pageIndex, pages, currentChapter, books, currentBookId });
    const lastManualNavAtRef = useRef<number>(0);

    useEffect(() => {
        readerStateRef.current = { pageIndex, pages, currentChapter, books, currentBookId };

        // Safety Guard: If page changed manually and we are still speaking (race condition), force stop.
        // We use a timestamp window (500ms) to prevent sticky state from killing future play sessions.
        if (isSpeaking) {
            const timeSinceNav = Date.now() - lastManualNavAtRef.current;
            if (timeSinceNav < 500) {
                console.log("[App] Safety Guard: Stopping TTS after manual nav (race detected)");
                stopSpeaking('manual_nav_guard');
            }
        }
    }, [pageIndex, pages, currentChapter, books, currentBookId, isSpeaking]);

    // Helper for next chapter/page logic (used by TTS and UI)
    const advancePageOrChapter = (reason: 'auto' | 'manual'): boolean => {
        const { pageIndex, pages, currentChapter, books, currentBookId } = readerStateRef.current;

        if (reason === 'manual') {
            lastManualNavAtRef.current = Date.now();
        }

        logTtsEvent('PAGE_NAV', { reason, action: 'attempt_next', whilePlaying: isSpeaking });

        if (pageIndex < pages.length - 1) {
            setPageIndex(p => p + 1);
            return true;
        } else {
            // Next Chapter Logic
            const nextCh = currentChapter + 1;
            // Try to find current Book Meta
            const bookMeta = books.find(b => b.id === currentBookId);
            const maxCh = (bookMeta as any)?.chapterCount;

            if (maxCh && nextCh > maxCh) {
                return false; // End of book
            }
            // Start Loading Next Chapter
            // Clear current data to prevent stale reads during "auto" transitions
            setVerseData(null);
            setCurrentChapter(nextCh);
            return true;
        }
    };

    // UI State for Chapter Nav
    const [showChapterSheet, setShowChapterSheet] = useState(false);

    // Auto-Advance Hook
    const tts = useTtsAutoAdvance({
        isPlaying: isSpeaking,
        setIsPlaying: setIsSpeaking,
        pageIndex,
        pages,
        verseData,
        isPaginating,
        advancePageOrChapter,
        onToast: setToastMsg,
        setIsBuffering,
        bookId: currentBookId,
        chapter: currentChapter,
        corpusId: activeCorpusId
    });

    const handleTtsToggle = (text: string[]) => {
        if (isSpeaking) {
            tts.stop();
        } else {
            tts.play(text);
        }
    };

    // Resize Listener
    useEffect(() => {
        const handler = () => setIsDesktop(window.innerWidth > 900);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    // Load Books on Corpus Change
    useEffect(() => {
        activeCorpus.load().then(() => {
            const secs = activeCorpus.getSections();
            setBooks(secs);
        });
    }, [activeCorpusId]);

    // Share Target Handler
    useEffect(() => {
        if (window.location.pathname === '/share') {
            // Basic extraction for now - just to acknowledge the share
            const params = new URLSearchParams(window.location.search);
            const title = params.get('title');
            const url = params.get('text') || params.get('url'); // Text often contains URL in Android share

            if (title || url) {
                setToastMsg(`Received Share: ${title || 'Content'}`);
                console.log('Share Target Data:', { title, url });
            }

            // Clear clean URL to avoid reload loop or stale state
            window.history.replaceState({}, '', '/');
        }
    }, []);

    // Book Selection & Resume Logic
    const handleSelectBook = (bookId: string) => {
        setCurrentBookId(bookId);
        setView('reader');

        // Auto-Resume if SmartReader is on (or default)
        if (smartReader) {
            const saved = loadLastRead(activeCorpusId, bookId);
            if (saved) {
                setCurrentChapter(saved.chapter);
                if (saved.pageIndex !== undefined) {
                    setPendingRestore({ page: saved.pageIndex });
                }
                setToastMsg(`Resumed at Ch ${saved.chapter}`);
            } else {
                setCurrentChapter(1);
                setPageIndex(0);
            }
        }
    };

    const handleJump = (bookId: string, chapter: number, verse?: number) => {
        setCurrentBookId(bookId);
        setCurrentChapter(chapter);
        setView('reader');
        setShowSettings(null);
        // Verse jump could be handled by auto-scrolling or highlighting later
    };

    const [isBookClubLoading, setIsBookClubLoading] = useState(false);

    // Load Chapter Content & restore page if needed
    useEffect(() => {
        if (!currentBookId || view !== 'reader') return;

        // Reset error state
        setBookClubError(null);

        // Check if this is a Book Club book
        const bookClubItem = audioManifest.find(m => m.id === currentBookId);

        console.log(`[Reader] Opening ${currentBookId}. Local checking... Remote manifest: ${!!bookClubItem}`);

        const fetchBookClubText = async () => {
            console.log(`[Reader] Fetching text for ${currentBookId}...`);
            setIsBookClubLoading(true);
            setIsPaginating(true); // Show spinner

            try {
                // 1. Check Offline Cache First (LibraryDB)
                const cached = await LibraryDB.getBookAsset(currentBookId);
                if (cached && cached.text) {
                    console.log("[Reader] Found text in offline cache");
                    processText(cached.text);
                    setIsBookClubLoading(false);
                    setIsPaginating(false);
                    return;
                }

                // 2. Fetch
                const r = await fetch(`/api/library/text/${currentBookId}`);
                if (!r.ok) {
                    // P0-F: Handle JSON errors kindly
                    const type = r.headers.get("Content-Type") || "";
                    if (type.includes("application/json")) {
                        const errJson = await r.json();
                        throw new Error(errJson.error || errJson.message || `Status ${r.status}`);
                    }
                    throw new Error(`Status ${r.status}`);
                }
                const text = await r.text();

                // Edge Case: If worker returns JSON {ok:false} with 200 (unlikely but possible with old code)
                if (text.startsWith('{"') && text.includes('"ok":false')) {
                    try {
                        const j = JSON.parse(text);
                        if (!j.ok) throw new Error(j.error || "API Error");
                    } catch (e) { /* ignore parse error if it's actually just text starting with {" */ }
                }

                if (!text || text.length < 100) throw new Error("Content too short or empty");

                // 3. Cache it (so next time we are offline-safe)
                await LibraryDB.saveBookAsset(currentBookId, text);

                processText(text);

            } catch (e: any) {
                console.error("Book Club Load Error", e);
                setBookClubError(e.message || "Failed to load");
                setVerseData(null);
            } finally {
                setIsBookClubLoading(false);
                setIsPaginating(false); // Stop spinner (if success or error)
            }
        };

        const processText = (text: string) => {
            // Simple chunking by double newline
            const paras = text.split(/\n\s*\n/);
            const vData: Record<string, string> = {};
            paras.forEach((p, i) => vData[String(i + 1)] = p.trim());

            setVerseData(vData);
            setPageIndex(0);
        };

        if (bookClubItem) {
            fetchBookClubText();
            return;
        }

        activeCorpus.load().then(() => {
            const data = activeCorpus.getChapter(currentBookId, currentChapter);
            if (data && Object.keys(data).length > 0) {
                console.log(`[Reader] Loaded local text for ${currentBookId}`);
                setVerseData(data);
                setPageIndex(0);
                setIsPaginating(true);
            } else {
                console.warn(`[Reader] Not found locally: ${currentBookId}. Attempting remote fetch fallback.`);
                fetchBookClubText(); // Attempt remote catch
            }
        });
    }, [currentBookId, currentChapter, activeCorpusId, view, audioManifest]);

    // Pagination & Restoration
    useEffect(() => {
        if (!verseData) {
            setPages([]);
            setIsPaginating(false);
            return;
        }

        const verses = Object.keys(verseData);
        // Heuristic: If empty (Cover?), make one page
        if (verses.length === 0) {
            setPages([[]]);
            setIsPaginating(false);
            return;
        }

        const newPages: string[][] = [];
        const CHUNK_SIZE = 4;
        for (let i = 0; i < verses.length; i += CHUNK_SIZE) {
            newPages.push(verses.slice(i, i + CHUNK_SIZE));
        }
        setPages(newPages);
        setIsPaginating(false);

        // Apply Restore
        if (pendingRestore) {
            if (pendingRestore.page < newPages.length) {
                setPageIndex(pendingRestore.page);
            }
            setPendingRestore(null);
        }
    }, [verseData, isDesktop]);

    // Persistence Effect
    useEffect(() => {
        if (!smartReader || !currentBookId || view !== 'reader' || !verseData) return;

        const save = () => {
            saveLastRead({
                corpusId: activeCorpusId,
                bookId: currentBookId,
                chapter: currentChapter,
                pageIndex: pageIndex,
                timestamp: Date.now()
            });
        };
        save();
    }, [currentBookId, currentChapter, pageIndex, smartReader, activeCorpusId]);

    // Haptics Helper
    const doHaptic = () => {
        if (haptics && !isSpeaking) triggerHaptic();
    };

    const handleNext = () => {
        doHaptic();
        if (isSpeaking) tts.stop('manual_page_next');
        advancePageOrChapter('manual');
    };

    const handlePrev = () => {
        doHaptic();
        lastManualNavAtRef.current = Date.now(); // Mark manual nav
        if (isSpeaking) tts.stop('manual_page_prev');
        if (pageIndex > 0) {
            setPageIndex(p => p - 1);
        } else if (currentChapter > 1) {
            setVerseData(null); // Invalidate for prev chapter too
            setCurrentChapter(c => c - 1);
        }
    };

    // Global Key Listener for Page Turns
    useEffect(() => {
        if (view !== 'reader') return;

        const handleKey = (e: KeyboardEvent) => {
            // Ignore if focus is in input
            const active = document.activeElement;
            if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || (active as HTMLElement).isContentEditable)) return;

            if (e.key === 'ArrowRight' || e.key === ' ') {
                e.preventDefault();
                handleNext();
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                handlePrev();
            }
        };

        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [view, pageIndex, currentChapter]); // Dependencies important for closure in handleNext? handleNext uses state.
    // Better: Helper functions inside effect or use updated refs?
    // handleNext uses 'readerStateRef' inside 'advancePageOrChapter'.
    // But 'handlePrev' uses state directly. 
    // To be safe, wrapped handlers use refs or specific deps.
    // 'handlePrev' uses pageIndex and currentChapter. So deps [pageIndex, currentChapter] are correct.

    // Stop speaking if unmounting or changing view
    useEffect(() => {
        return () => {
            if (isSpeaking) stopSpeaking();
        };
    }, []);

    const closeSettings = () => setShowSettings(null);

    const refreshLibrary = () => {
        fetch('/api/library/manifest').then(r => r.json()).then(d => {
            if (d.ok && d.items) {
                setAudioManifest(d.items);
                setToastMsg("Library refreshed");
            }
        }).catch(e => {
            console.error(e);
            setToastMsg("Refresh failed");
        });

        fetch('/api/entitlement').then(r => r.json()).then(d => {
            if (typeof d.isPro === 'boolean') setIsBookClubMember(d.isPro);
        }).catch(e => console.warn('Entitlement check failed (soft):', e));
    };

    const handlePlayAudio = (bookId: string) => {
        const book = books.find(b => b.id === bookId);
        let bookAudio = audioManifest.find(m => m.id === bookId);

        // Legacy Siddhartha fallback
        if (!bookAudio && book?.title?.toLowerCase().includes('siddhartha')) {
            bookAudio = audioManifest.find(m => m.id === 'siddhartha');
        }

        if (bookAudio) {
            setAudioOverlay({
                url: bookAudio.audioUrl || bookAudio.playlistUrl || `/api/library/playlist/${bookAudio.id}`,
                title: bookAudio.title,
                coverUrl: bookAudio.coverUrl,
                startTime: (bookAudio as any).trimStartSeconds,
                bookId: bookAudio.id,
                isMember: isMember,
                upsellUrl: 'https://corpora-content.b-cdn.net/upsell_v1.mp3' // Placeholder P0-D
            });
        } else {
            setToastMsg("Audio not found");
        }
    };

    return (
        <ErrorBoundary>
            <div className={`app-container ${theme}`} style={{ height: '100dvh', display: 'flex', flexDirection: 'column', background: 'var(--surface-color)', color: 'var(--text-color)' }}>
                {/* HEADER */}
                <header style={{
                    height: '50px', display: 'flex', alignItems: 'center', padding: '0 10px',
                    borderBottom: '1px solid var(--border-color)', justifyContent: 'space-between',
                    background: 'var(--surface-color)', zIndex: 10, position: 'relative'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', zIndex: 20 }}>
                        {/* Home Button (My Library) - Always show unless already there */}
                        {view !== 'library' && (
                            <button onClick={() => {
                                if (isSpeaking) tts.stop('manual_home_nav');
                                setView('library');
                            }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '5px' }} title="My Library">
                                🏠
                            </button>
                        )}

                        {/* Discover Button - ALWAYS VISIBLE if not in Discover */}
                        <button onClick={() => setView('discover')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '5px', display: 'flex', alignItems: 'center', gap: '5px' }} title="Search / Discover">
                            🔍 <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Discover</span>
                        </button>

                        {!view || view === 'library' || view === 'discover' ? (
                            <div style={{ fontWeight: 900, fontSize: '1.2rem', letterSpacing: '-0.5px', marginLeft: '10px' }}>WiKiBoox</div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {/* Chapter Navigation */}
                                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{currentBookId}</span>
                                    {(() => {
                                        const book = books.find(b => b.id === currentBookId);
                                        const maxCh = (book as any)?.chapterCount || 1;
                                        const noChapters = maxCh <= 1;

                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                {!noChapters && (
                                                    <button
                                                        disabled={currentChapter <= 1}
                                                        onClick={() => {
                                                            lastManualNavAtRef.current = Date.now();
                                                            if (isSpeaking) tts.stop('manual_chapter_nav');
                                                            setCurrentChapter(c => Math.max(1, c - 1));
                                                        }}
                                                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', opacity: currentChapter <= 1 ? 0.3 : 1 }}
                                                    >
                                                        ◀
                                                    </button>
                                                )}

                                                <span
                                                    onClick={() => {
                                                        if (noChapters) return;
                                                        if (isSpeaking) tts.stop('manual_chapter_sheet');
                                                        setShowChapterSheet(true);
                                                    }}
                                                    style={{
                                                        fontSize: '0.8rem',
                                                        opacity: 0.8,
                                                        cursor: noChapters ? 'default' : 'pointer',
                                                        textDecoration: noChapters ? 'none' : 'underline',
                                                        textUnderlineOffset: '2px'
                                                    }}
                                                >
                                                    {noChapters ? ((book as any)?.title || 'Book') : `Chapter ${currentChapter}`}
                                                </span>

                                                {!noChapters && (
                                                    <button
                                                        disabled={currentChapter >= maxCh}
                                                        onClick={() => {
                                                            lastManualNavAtRef.current = Date.now();
                                                            if (isSpeaking) tts.stop('manual_chapter_nav');
                                                            if (currentChapter < maxCh) {
                                                                setCurrentChapter(c => c + 1);
                                                            }
                                                        }}
                                                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', opacity: currentChapter >= maxCh ? 0.3 : 1 }}
                                                    >
                                                        ▶
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Floating Play Button (Southwest) - Always Visible in Reader */}
                    {view === 'reader' && (
                        <div style={{
                            position: 'fixed', bottom: '30px', left: '20px',
                            zIndex: 90, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    if (isSpeaking || isBuffering || tts.isPending || isPaginating) {
                                        tts.stop('stop_button');
                                        return;
                                    }

                                    // Play Logic
                                    // If it's a Book Club title, ALWAYS use the audio player overlay (Part A Requirement)
                                    // We check if audio exists for this book.
                                    const book = books.find(b => b.id === currentBookId);
                                    let bookAudio = audioManifest.find(m => m.id === currentBookId);
                                    if (!bookAudio && book?.title?.toLowerCase().includes('siddhartha')) {
                                        bookAudio = audioManifest.find(m => m.id === 'siddhartha');
                                    }

                                    if (bookAudio) {
                                        handlePlayAudio(currentBookId);
                                    } else {
                                        // Default TTS (Always fallback to strict TTS if not a Book Club title)
                                        const text = pages[pageIndex]?.map(k => verseData && verseData[k]).filter(Boolean) as string[] || [];
                                        if (text.length) {
                                            tts.play(text);
                                        } else {
                                            setToastMsg("No text to speak on this page");
                                        }
                                    }
                                }}
                                style={{
                                    width: '64px', height: '64px', borderRadius: '50%',
                                    background: 'var(--surface-color)',
                                    border: `3px solid ${isBuffering ? '#fa0' : ((isSpeaking || tts.isPending || isPaginating) ? '#4CAF50' : 'var(--border-color)')}`,
                                    color: 'var(--text-color)',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '1.6rem', padding: 0,
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    pointerEvents: 'auto', marginBottom: '4px',
                                    userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none'
                                }}
                                aria-label={isSpeaking ? "Stop" : "Play"}
                            >
                                {(isSpeaking || isBuffering || tts.isPending || isPaginating) ?
                                    ((isBuffering || isPaginating) ? '⏳' : '■') :
                                    '▶'}
                            </button>
                            {!isSpeaking && resumeHint && (
                                <div style={{
                                    background: 'var(--surface-color)', padding: '2px 6px',
                                    borderRadius: '4px', fontSize: '0.65rem', color: 'var(--accent-color)',
                                    border: '1px solid var(--border-color)', pointerEvents: 'auto',
                                    maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    position: 'absolute', bottom: '100%', marginBottom: '5px', left: '0'
                                }}>
                                    {resumeHint}
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '15px', zIndex: 20 }}>
                        {view === 'reader' && (
                            <div style={{ position: 'relative' }}>
                                <button onClick={() => setShowBookMenu(!showBookMenu)} style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: '0 5px' }}>⋮</button>
                                {showBookMenu && (
                                    <>
                                        <div style={{ position: 'fixed', inset: 0, zIndex: 90 }} onClick={() => setShowBookMenu(false)} />
                                        <div style={{
                                            position: 'absolute', top: '100%', right: 0,
                                            background: 'var(--surface-color)', border: '1px solid var(--border-color)',
                                            borderRadius: '8px', zIndex: 100, minWidth: '180px',
                                            boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                                            display: 'flex', flexDirection: 'column', overflow: 'hidden'
                                        }}>
                                            {(() => {
                                                // Optional: Other menu items or just empty if we removed everything
                                                // The user asked to remove "Play Narrated".
                                                return null;
                                            })()}


                                            <button onClick={() => {
                                                // Force reload remote
                                                setVerseData(null);
                                                setBookClubError(null);
                                                // We need to trigger the useEffect again or call fetch directly.
                                                // Toggling view is dirty but works, or we can expose the fetcher.
                                                // Simplest: just reload window for now? No, unmount/remount reader.
                                                setView('library');
                                                setTimeout(() => setView('reader'), 50);
                                            }} style={{ padding: '12px', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--text-color)' }}>
                                                🔄 Reload Text (Force)
                                            </button>

                                            <button onClick={() => {
                                                clearLastRead(activeCorpusId, currentBookId);
                                                clearTtsProgress(activeCorpusId, currentBookId);
                                                setCurrentChapter(1);
                                                setPageIndex(0);
                                                setToastMsg("Restarted book");
                                                setShowBookMenu(false);
                                            }} style={{ padding: '12px', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--text-color)' }}>
                                                ↺ Restart Book
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {view === 'reader' && (
                            <button onClick={() => setShowChat(!showChat)} style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>
                                {showChat ? '✖️' : '💬'}
                            </button>
                        )}

                        {view !== 'reader' && (
                            <button onClick={() => setView('help')} style={{ background: 'transparent', border: 'none', color: 'var(--text-color)', cursor: 'pointer', fontSize: '1.2rem', padding: '0 5px' }} title="Help">❓</button>
                        )}

                        {/* Import - Only on Library View, Gated by Sources */}
                        {view === 'library' && (() => {
                            const canImport = localStorage.getItem('source_pg_canada') === 'true' || localStorage.getItem('source_pg_australia') === 'true';
                            return (
                                <button
                                    onClick={() => {
                                        if (canImport) setShowImportModal(true);
                                        else alert("Enable a source in Settings → Sources to import.");
                                    }}
                                    style={{
                                        background: 'transparent', border: 'none',
                                        color: canImport ? 'var(--text-secondary)' : 'var(--text-disabled)',
                                        cursor: canImport ? 'pointer' : 'not-allowed',
                                        fontSize: '0.9rem', padding: '0 5px',
                                        opacity: canImport ? 1 : 0.5
                                    }}
                                    title={canImport ? "Import Book" : "Enable a source in Settings → Sources to import"}
                                >
                                    Import
                                </button>
                            );
                        })()}

                        <button onClick={() => setShowSettings('general')} style={{ background: 'transparent', border: 'none', color: 'var(--text-color)', cursor: 'pointer', fontSize: '1.2rem' }}>⚙️</button>
                    </div>
                </header>

                {/* VIEW CONTENT - Flex Layout for Desktop Chat */}
                <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
                    <main style={{ flex: 1, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                        {bookClubError && view === 'reader' && (
                            <div style={{
                                position: 'absolute', inset: 0, zIndex: 100, background: 'var(--bg-color)',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px'
                            }}>
                                <h3>⚠️ Could not load book</h3>
                                <p style={{ color: 'red' }}>{bookClubError}</p>
                                <button onClick={() => window.location.reload()} style={{ padding: '10px 20px', fontSize: '1.2rem' }}>
                                    Retry
                                </button>
                                <button onClick={() => setView('library')} style={{ padding: '10px 20px' }}>
                                    Back to Library
                                </button>
                            </div>
                        )}

                        {view === 'library' && <LibraryView
                            activeCorpusId={activeCorpusId}
                            onOpenBook={handleSelectBook} // FIX: Was onSelectBook, which caused undefined prop in LibraryView
                            onDiscover={() => setView('discover')}
                            onHelp={() => setView('help')}
                            isPro={isMember}
                            audioManifest={audioManifest} // Keeping strictly to match existing potentially used "audioManifest" in unknown props
                            manifest={audioManifest} // Satisfy required 'manifest' prop
                        />}

                        {view === 'admin' && <AdminFactory />}
                        {view === 'admin-console' && <DeveloperConsole />}

                        {view === 'reader' && (
                            <ReaderFlipView
                                pages={pages}
                                verseData={verseData || {}}
                                onNext={handleNext}
                                onPrev={handlePrev}
                                isPaginating={isPaginating}
                                isSpeaking={isSpeaking}
                                onSpeakToggle={(text) => handleTtsToggle(text)}
                                pageIndex={pageIndex}
                                onGoToLibrary={() => setView('library')}
                            />
                        )}

                        {view === 'discover' && <DiscoverView
                            onGoToLibrary={() => setView('library')}
                            onOpenBook={handleSelectBook}
                            onPlayAudio={handlePlayAudio}
                            manifest={audioManifest || []} // Pass manifest explicitly as expected by DiscoverView
                            onToast={setToastMsg}
                            isMember={isMember}
                        />}

                        {view === 'help' && <HelpView />}
                    </main>

                    {/* Render Chat Drawer Properly */}
                    {showChat && (
                        <div style={isDesktop ? {
                            width: '350px',
                            borderLeft: '1px solid var(--border-color)',
                            background: 'var(--surface-color)',
                            zIndex: 20
                        } : {
                            position: 'fixed', top: '50px', bottom: 0, right: 0,
                            width: '100%',
                            zIndex: 150,
                            background: 'var(--surface-color)'
                        }}>
                            {/* @ts-ignore */}
                            <ChatDrawer
                                onClose={() => setShowChat(false)}
                                onOpenSettings={() => { setShowChat(false); setShowSettings('ai'); }}
                                currentVerses={verseData ? Object.entries(verseData).map(([k, v]) => ({ num: k, text: v })) : []}
                                currentRef={`${currentBookId} ${currentChapter}`}
                                onNavigate={handleJump}
                                messages={chatMessages}
                                onMessagesChange={setChatMessages}
                                studyMode={false}
                                onToggleStudyMode={() => { }}
                                corpusName={activeCorpus.title}
                                // Context Props
                                isPublicDomain={['public_domain_gutendex', 'siddhartha', 'douay_rheims'].includes(activeCorpus.id)}
                                contextText={pages && pages[pageIndex] ? pages[pageIndex].join('\n') : undefined}
                                bookTitle={books.find(b => b.id === currentBookId)?.title}
                                author={(books.find(b => b.id === currentBookId) as any)?.author}
                                chapterTitle={`Chapter ${currentChapter}`}
                                pageLabel={pages.length > 0 ? `${Math.floor((pageIndex + 1) / pages.length * 100)}%` : ''}
                            />
                        </div>
                    )}
                </div>

                {/* Toast */}
                {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg(null)} />}

                {/* PWA Update Prompt */}
                {needRefresh && (
                    <div style={{
                        position: 'fixed', bottom: 20, right: 20, zIndex: 3000,
                        background: 'var(--surface-color)', border: '1px solid var(--border-color)',
                        padding: '15px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        display: 'flex', gap: '10px', alignItems: 'center'
                    }}>
                        <span>New version available!</span>
                        <button onClick={() => updateServiceWorker(true)} style={{
                            background: 'var(--accent-color)', border: 'none', color: 'white',
                            padding: '5px 12px', borderRadius: '4px', cursor: 'pointer'
                        }}>Reload</button>
                    </div>
                )}

                {/* Settings Modal */}
                {showSettings && (
                    <SettingsModal
                        onClose={closeSettings}
                        initialTab={showSettings === 'debug' ? 'experimental' : showSettings as any}
                        readerMode={readerMode}
                        setReaderMode={setReaderMode}
                        haptics={haptics}
                        setHaptics={setHaptics}
                        onJump={handleJump}
                        onReindex={async () => {
                            const c = CorpusService.getActive();
                            if (c && 'reindex' in c) await (c as any).reindex();
                        }}
                        onRefreshLibrary={refreshLibrary}
                        onHome={() => setView('library')}
                        onToast={setToastMsg}
                        pwaProps={pwa}
                        debugInfo={{ view, currentBookId, chapter: currentChapter, activeCorpusId, textPreview: pages[pageIndex]?.join(' ').slice(0, 50) }}
                        wakeLock={wakeLock}
                        isMember={isMember}
                        onToggleMember={() => setDebugMemberOverride(!debugMemberOverride)}
                    />
                )}

                {/* Chapter Sheet Overlay */}
                {showChapterSheet && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', zIndex: 2000,
                        display: 'flex', justifyContent: 'center', alignItems: 'flex-end'
                    }} onClick={() => setShowChapterSheet(false)}>
                        <div style={{
                            background: 'var(--surface-color)', width: '100%', maxWidth: '500px',
                            maxHeight: '60vh', borderTopLeftRadius: '16px', borderTopRightRadius: '16px',
                            display: 'flex', flexDirection: 'column', overflow: 'hidden',
                            boxShadow: '0 -4px 12px rgba(0,0,0,0.3)', marginBottom: isDesktop ? '20vh' : 0,
                            borderRadius: isDesktop ? '12px' : undefined
                        }} onClick={e => e.stopPropagation()}>
                            <div style={{ padding: '15px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 600 }}>Select Chapter</span>
                                <button onClick={() => setShowChapterSheet(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
                            </div>
                            <div style={{ overflowY: 'auto', padding: '10px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))', gap: '10px' }}>
                                {(() => {
                                    const book = books.find(b => b.id === currentBookId);
                                    const count = (book as any)?.chapterCount || 1;
                                    return Array.from({ length: count }, (_, i) => i + 1).map(ch => (
                                        <button key={ch} onClick={() => {
                                            lastManualNavAtRef.current = Date.now();
                                            if (isSpeaking) tts.stop('manual_chapter_select');
                                            setCurrentChapter(ch);
                                            setShowChapterSheet(false);
                                        }} style={{
                                            padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)',
                                            background: currentChapter === ch ? 'var(--accent-color)' : 'var(--surface-color)',
                                            color: currentChapter === ch ? '#fff' : 'var(--text-color)',
                                            cursor: 'pointer'
                                        }}>
                                            {ch}
                                        </button>
                                    ));
                                })()}
                            </div>
                        </div>
                    </div>
                )}

                {/* Audio Overlay (Global) */}
                {audioOverlay && (
                    <AudioPlayerOverlay
                        url={audioOverlay.url}
                        title={audioOverlay.title}
                        coverUrl={audioOverlay.coverUrl}
                        startTime={(audioOverlay as any).startTime}
                        bookId={audioOverlay.bookId}
                        onClose={() => setAudioOverlay(null)}
                        isMember={(audioOverlay as any).isMember}
                        upsellUrl={(audioOverlay as any).upsellUrl}
                    />
                )}

                {showImportModal && (
                    <ImportModal
                        onClose={() => setShowImportModal(false)}
                        onBookImported={(id) => {
                            setShowImportModal(false);
                            handleSelectBook(id);
                        }}
                    />
                )}

                {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg(null)} />}
            </div>
        </ErrorBoundary>
    );
};

export default App;
